/**
 * Shared UI component for UI Select.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { OverlayOptions } from 'primeng/api';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';
import { Select, SelectModule } from 'primeng/select';
import { fromEvent } from 'rxjs';

import { UiSelectOption } from './ui-select.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { resolveTranslatedUiLabel } from '../ui-label-translate.util';

const CLOSE_OTHERS_EVENT = 'ui-select:close-others';

interface UiPrimeSelectOption extends UiSelectOption {
  translatedLabel: string;
}

/**
 * COMPONENT: Ui Select
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-select',
  standalone: true,
  imports: [FormsModule, MultiSelectModule, SelectModule, UiLabelTranslatePipe],
  templateUrl: './ui-select.component.html',
  styleUrl: './ui-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiSelectComponent {
  private readonly translate = inject(TranslateService);
  /** STATE OWNER: languageVersion documents the signal state boundary used by this file. */
  private readonly languageVersion = signal(0);
  readonly selectRef = viewChild<Select>('selectRef');
  readonly multiSelectRef = viewChild<MultiSelect>('multiSelectRef');

  /** PARENT-CHILD CONTRACT: label documents the input state boundary used by this file. */
  readonly label = input('');
  /** PARENT-CHILD CONTRACT: id documents the input state boundary used by this file. */
  readonly id = input('');
  readonly value = input<string>('');
  readonly values = input<string[]>([]);
  /** PARENT-CHILD CONTRACT: placeholder documents the input state boundary used by this file. */
  readonly placeholder = input('Select option');
  /** PARENT-CHILD CONTRACT: searchable documents the input state boundary used by this file. */
  readonly searchable = input(false);
  /** PARENT-CHILD CONTRACT: searchPlaceholder documents the input state boundary used by this file. */
  readonly searchPlaceholder = input('Search');
  /** PARENT-CHILD CONTRACT: showPlaceholderOption documents the input state boundary used by this file. */
  readonly showPlaceholderOption = input(true);
  readonly options = input<UiSelectOption[]>([]);
  /** PARENT-CHILD CONTRACT: disabled documents the input state boundary used by this file. */
  readonly disabled = input(false);
  /** PARENT-CHILD CONTRACT: required documents the input state boundary used by this file. */
  readonly required = input(false);
  /** PARENT-CHILD CONTRACT: validateRequired documents the input state boundary used by this file. */
  readonly validateRequired = input(true);
  /** PARENT-CHILD CONTRACT: multiple documents the input state boundary used by this file. */
  readonly multiple = input(false);
  /** PARENT-CHILD CONTRACT: clearable documents the input state boundary used by this file. */
  readonly clearable = input(true);
  /** PARENT-CHILD CONTRACT: closeOnSelect documents the input state boundary used by this file. */
  readonly closeOnSelect = input(false);
  /** PARENT-CHILD CONTRACT: toggleOnTriggerClick documents the input state boundary used by this file. */
  readonly toggleOnTriggerClick = input(false);
  /** PARENT-CHILD CONTRACT: loadMoreOnScroll documents the input state boundary used by this file. */
  readonly loadMoreOnScroll = input(false);
  /** PARENT-CHILD CONTRACT: loadingMoreOptions documents the input state boundary used by this file. */
  readonly loadingMoreOptions = input(false);
  /** PARENT-CHILD CONTRACT: hasMoreOptions documents the input state boundary used by this file. */
  readonly hasMoreOptions = input(false);
  /** PARENT-CHILD CONTRACT: virtualScrollItemSize documents the input state boundary used by this file. */
  readonly virtualScrollItemSize = input(40);
  /** PARENT-CHILD CONTRACT: error documents the input state boundary used by this file. */
  readonly error = input('');
  /** PARENT-CHILD CONTRACT: hint documents the input state boundary used by this file. */
  readonly hint = input('');
  readonly valueChange = output<string>();
  readonly valuesChange = output<string[]>();
  readonly loadMoreOptions = output<void>();
  readonly searchChange = output<string>();
  private readonly lastLoadMoreOptionCount = signal<number | null>(null);
  /** PrimeNG overlay settings keep dropdown layering high without deprecated z-index inputs. */
  readonly overlayOptions: OverlayOptions = { autoZIndex: true, baseZIndex: 3000 };
  readonly resolvedOptions = computed<UiPrimeSelectOption[]>(() => {
    this.languageVersion();

    return this.options().map((option) => ({
      ...option,
      translatedLabel: this.resolveLabel(option.label)
    }));
  });

  /**
   * BUSINESS LOGIC: constructor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
      this.languageVersion.update((version) => version + 1);
    });

    /** STATE OWNER: reactiveEffect documents the effect state boundary used by this file. */
    effect(() => {
      this.resolvedOptions().length;

      if (!this.loadingMoreOptions()) {
        this.lastLoadMoreOptionCount.set(null);
      }
    });

    fromEvent<CustomEvent>(document, CLOSE_OTHERS_EVENT)
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (event.detail !== this) {
          this.selectRef()?.hide();
          this.multiSelectRef()?.hide();
        }
      });
  }

  /**
   * BUSINESS LOGIC: onShow
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onShow(): void {
    document.dispatchEvent(new CustomEvent(CLOSE_OTHERS_EVENT, { detail: this }));
  }

  /**
   * PARENT-CHILD CONTRACT: paged dropdown parents own API page state.
   * The shared wrapper only reports when PrimeNG's virtual scroller reaches the
   * currently loaded edge so parents can append the next page.
   */
  onLazyLoad(event: { first?: number; last?: number }): void {
    if (!this.loadMoreOnScroll() || this.loadingMoreOptions() || !this.hasMoreOptions()) {
      return;
    }

    const loadedCount = this.resolvedOptions().length;
    const lastVisibleIndex = event.last ?? 0;

    if (loadedCount > 0 && lastVisibleIndex >= loadedCount - 2) {
      if (this.lastLoadMoreOptionCount() === loadedCount) {
        return;
      }

      this.lastLoadMoreOptionCount.set(loadedCount);
      this.loadMoreOptions.emit();
    }
  }

  /**
   * BUSINESS LOGIC: onValueChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onValueChange(value: string | null | undefined): void {
    this.valueChange.emit(value ?? '');
  }

  /**
   * BUSINESS LOGIC: onValuesChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onValuesChange(values: string[] | null | undefined): void {
    this.valuesChange.emit(values ?? []);

    if (this.closeOnSelect()) {
      this.multiSelectRef()?.hide();
    }
  }

  /**
   * BUSINESS LOGIC: onSearchChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onSearchChange(event: { filter?: string }): void {
    this.searchChange.emit(event.filter ?? '');
  }

  /**
   * BUSINESS LOGIC: onMultiSelectClick
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onMultiSelectClick(): void {
    if (!this.toggleOnTriggerClick()) {
      return;
    }

    const multiSelect = this.multiSelectRef();
    if (multiSelect?.overlayVisible) {
      multiSelect.hide();
    }
  }

  /**
   * BUSINESS LOGIC: resolveLabel
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private resolveLabel(value: string): string {
    return resolveTranslatedUiLabel(this.translate, value);
  }
}
